import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext.tsx';

export interface TourStep {
  id: string;
  targetSelector: string; // CSS selector or data attribute to highlight
  title: string;
  description: string;
  badge?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  actionHint?: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    targetSelector: 'header',
    title: 'Welcome to IHB Pulse!',
    description:
      'Your high-performance project & operations management platform. Let us show you around the core features in 5 quick steps so you feel right at home.',
    badge: 'Step 1 of 5',
    placement: 'center',
    actionHint: 'Click Next to begin the guided tour.',
  },
  {
    id: 'multiview-dropdown',
    targetSelector: '[data-tour="multiview-selector"]',
    title: 'Unified Multi-View Engine',
    description:
      'Switch between Kanban Boards, Grid Spreadsheets, Gantt Critical Path timelines, Mind Maps, and Calendar views directly from this selector without jumping pages.',
    badge: 'Step 2 of 5',
    placement: 'bottom',
    actionHint: 'Pick any view format that best fits your workflow.',
  },
  {
    id: 'new-task',
    targetSelector: '[data-tour="new-task-btn"]',
    title: 'Rapid Action Item Creation',
    description:
      'Create tasks instantly with custom properties, tags, priority levels, estimated hours, and file attachments without breaking your flow.',
    badge: 'Step 3 of 5',
    placement: 'bottom',
    actionHint: 'Or press Ctrl+K / ⌘K anywhere to open the command palette.',
  },
  {
    id: 'live-call',
    targetSelector: '[data-tour="live-call-card"]',
    title: 'Built-in 1-on-1 & Group Video Calls',
    description:
      'Start encrypted video conferences or 1-on-1 calls with active speaker detection, screen sharing, meeting notes, and a floating Picture-in-Picture mode.',
    badge: 'Step 4 of 5',
    placement: 'top',
    actionHint: 'Launch instant syncs from the dashboard or chat channels.',
  },
  {
    id: 'sidebar-nav',
    targetSelector: '[data-tour="sidebar-nav"]',
    title: 'Deep Workspace Modules',
    description:
      'Explore Projects, Workload Heatmaps, Team Discussions, Asset Proofing, and Security Audit logs from the sidebar. You are all set to start managing projects!',
    badge: 'Step 5 of 5',
    placement: 'right',
    actionHint: 'Click Finish Tour to explore freely.',
  },
];

interface OnboardingContextType {
  isTourActive: boolean;
  currentStepIndex: number;
  currentStep: TourStep;
  totalSteps: number;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  resetOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType>({
  isTourActive: false,
  currentStepIndex: 0,
  currentStep: TOUR_STEPS[0],
  totalSteps: TOUR_STEPS.length,
  startTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipTour: () => {},
  resetOnboarding: () => {},
});

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Auto-launch the tutorial once per account. Whether it was already completed is stored
  // on the server, so a new browser, device or incognito window does not show it again.
  useEffect(() => {
    if (!user?.id || !token) {
      setIsTourActive(false);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    (async () => {
      try {
        const res = await fetch('/api/auth/onboarding', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || data.completed) return;
        // Small timeout to allow DOM elements and layout to mount cleanly
        timer = setTimeout(() => {
          setCurrentStepIndex(0);
          setIsTourActive(true);
        }, 700);
      } catch (err) {
        console.warn('Could not load onboarding status:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [user?.id, token]);

  const startTour = () => {
    setCurrentStepIndex(0);
    setIsTourActive(true);
  };

  const nextStep = () => {
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      skipTour();
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const skipTour = () => {
    setIsTourActive(false);
    if (token) {
      fetch('/api/auth/onboarding/complete', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch((err) => console.warn('Could not save onboarding status:', err));
    }
  };

  const resetOnboarding = () => {
    startTour();
  };

  return (
    <OnboardingContext.Provider
      value={{
        isTourActive,
        currentStepIndex,
        currentStep: TOUR_STEPS[currentStepIndex],
        totalSteps: TOUR_STEPS.length,
        startTour,
        nextStep,
        prevStep,
        skipTour,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => useContext(OnboardingContext);
